import React, { useState } from "react";
import { Mail, Phone, MapPin, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../utils/api";
import SeoHead from "../components/SeoHead";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <article>
      <SeoHead
        title="Contact"
        description="Have questions about Paysafi? Reach out to our team. We'd love to help you manage your rental properties more effectively."
        path="/contact"
      />
      <section className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
            Get in touch
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Have questions about Paysafi? We'd love to hear from you.
          </p>
        </div>
      </section>
      <section className="py-14 sm:py-20">
        <div className="mx-auto grid max-w-5xl gap-10 px-4 sm:px-6 sm:gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Contact details</h2>
            <div className="mt-6 space-y-5">
              <ContactItem icon={Mail} label="Email" value="botibongasolutions@gmail.com" />
              <ContactItem icon={Phone} label="Phone" value="+254 717 811 921" />
              <ContactItem icon={MapPin} label="Location" value="Nairobi, Kenya" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Send us a message</h2>
            {status === "sent" ? (
              <div className="mt-6 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                Thanks! We'll get back to you soon.
              </div>
            ) : status === "error" ? (
              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <XCircle className="h-4 w-4" />
                  Failed to send message. Please try again.
                </div>
                <button className="btn-secondary text-sm" onClick={() => setStatus("idle")}>Try again</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="label-base">Name</label>
                  <input
                    className="input-base"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label-base">Email</label>
                  <input
                    type="email"
                    className="input-base"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label-base">Message</label>
                  <textarea
                    className="input-base min-h-[120px] resize-y"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={status === "sending"}
                >
                  {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send message
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </article>
  );
}

function ContactItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
        <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">{value}</div>
      </div>
    </div>
  );
}
